import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { logger } from "@/utils/logger";
import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/hooks/useAuthStore";
import { acceptInviteToken } from "@/utils/inviteUtils";
import { track } from "@/lib/posthog";
import {
  AUTH_FIELD_CLASSES,
  AUTH_LABEL_CLASSES,
  AUTH_LINK_CLASSES,
  AUTH_PRIMARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";

type FormSchema = z.infer<ReturnType<typeof createFormSchema>>;

function createFormSchema(formTranslations: { validation: { orgNameMinLength: string } }) {
  return z.object({
    orgName: z.string().min(2, { message: formTranslations.validation.orgNameMinLength }),
  });
}

interface PendingInvite {
  id: number;
  invite_token: string;
  invitee_email: string;
  invited_role: string;
  status: string;
  expires_at: string;
  created_at: string;
  org_id: string;
  organisations?: {
    name: string;
    domain?: string;
  } | null;
}

export default function CreateOrganization() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { signOut } = useAuth();
  const authStore = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [sessionEmail, setSessionEmail] = useState("");
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [acceptingInvite, setAcceptingInvite] = useState<string | null>(null);

  const formSchema = createFormSchema(t.forms);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      orgName: "",
    },
  });

  useEffect(() => {
    const checkAuthAndInvites = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
      setSessionEmail(session.user.email ?? "");

      // Fetch pending invites for the current user
      try {
        const { data: invites, error } = await supabase
          .from("organization_invites")
          .select(
            `
            id,
            invite_token,
            invitee_email,
            invited_role,
            status,
            expires_at,
            created_at,
            org_id,
            organisations(
              name,
              domain
            )
          `
          )
          .eq("invitee_email", session.user.email?.toLowerCase().trim())
          .eq("status", "pending")
          .gt("expires_at", new Date().toISOString())
          .order("created_at", { ascending: false });

        if (!error && invites) {
          setPendingInvites(invites);
        }
      } catch (error) {
        logger.error(
          "auth",
          "fetch-error",
          error instanceof Error ? error.message : "Unknown error"
        );
      } finally {
        setLoadingInvites(false);
      }
    };
    checkAuthAndInvites();
  }, [navigate]);

  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      toast({
        variant: "destructive",
        title: t.common.error,
        description: t.auth.failedToSignOut,
      });
    } finally {
      setIsLoggingOut(false);
    }
  }

  async function handleAcceptInvite(invite: PendingInvite) {
    setAcceptingInvite(invite.invite_token);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error(t.auth.authenticationRequired);
      }

      const result = await acceptInviteToken(invite.invite_token, session.user.id);

      if (result.success) {
        // This page is only reachable with a session, so the account existed.
        track("invite_accepted", { role: invite.invited_role, had_account: true });
        toast({
          title: t.common.success,
          description: t.auth.joinedOrgAsRole
            .replace("{organizationName}", invite.organisations?.name || invite.org_id)
            .replace("{role}", invite.invited_role),
        });

        // Refresh session to update JWT with new org membership
        await supabase.auth.refreshSession();

        // Reload AuthStore to refetch org roles with updated JWT
        await authStore.loadSession();

        // Now navigate - ProtectedRoute will see the updated organizationCount
        navigate("/buildings");
      } else {
        throw new Error(result.error || t.auth.failedToAcceptInvitation);
      }
    } catch (error) {
      logger.error(
        "auth",
        "accept-error",
        error instanceof Error ? error.message : "Unknown error"
      );
      toast({
        variant: "destructive",
        title: t.common.error,
        description: error instanceof Error ? error.message : t.auth.unableToProcessInvitation,
      });
    } finally {
      setAcceptingInvite(null);
    }
  }

  async function onSubmit(values: FormSchema) {
    setIsLoading(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error(t.auth.authenticationRequired);
      }

      const { data: profileData, error: profileError } = await supabase
        .from("user_profiles")
        .select("id")
        .eq("auth_user_id", session.user.id)
        .single();

      if (profileError || !profileData) {
        throw new Error(t.auth.userNotFound);
      }

      const profileId = profileData.id;
      const orgId = uuidv4();

      logger.info("organization", "create-start", `Creating organization: ${values.orgName}`);

      const { error: orgError } = await supabase.from("organisations").insert({
        id: orgId,
        name: values.orgName,
        owner_id: profileId,
        created_by: profileId,
      });

      if (orgError) {
        logger.error("organization", "create-org-error", orgError.message);
        throw new Error(t.auth.failedToCreateOrganization);
      }

      const { error: roleError } = await supabase.from("org_user_roles").insert({
        user_id: profileId,
        org_id: orgId,
        role: "org_owner",
        created_by: profileId,
      });

      if (roleError) {
        logger.error("organization", "create-role-error", roleError.message);
        throw new Error(t.auth.failedToAssignOwnerRole);
      }

      logger.info("organization", "create-success", "Organization created successfully");
      track("organization_created", { source: "onboarding" });

      toast({
        title: t.common.success,
        description: t.auth.organizationCreated.replace("{orgName}", values.orgName),
      });

      // Refresh session to update JWT with new org membership
      await supabase.auth.refreshSession();

      // Reload AuthStore to refetch org roles with updated JWT
      await authStore.loadSession();

      // Now navigate - ProtectedRoute will see the updated organizationCount
      navigate("/buildings");
    } catch (error) {
      logger.error(
        "organization",
        "create-error",
        error instanceof Error ? error.message : "Unknown error"
      );

      toast({
        variant: "destructive",
        title: t.common.error,
        description: error instanceof Error ? error.message : t.forms.error.unexpectedError,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthPageLayout contentClassName="max-w-[460px]">
      <AuthPageHeader
        title={t.forms.createOrganization.title}
        description={t.forms.createOrganization.description}
      />

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-[18px]" noValidate>
        <div className="space-y-2">
          <Label htmlFor="orgName" className={AUTH_LABEL_CLASSES}>
            {t.forms.createOrganization.name}
          </Label>
          <Input
            id="orgName"
            placeholder={t.forms.createOrganization.placeholder}
            type="text"
            autoComplete="organization"
            className={AUTH_FIELD_CLASSES}
            aria-required="true"
            aria-invalid={!!form.formState.errors.orgName}
            aria-describedby={form.formState.errors.orgName ? "orgName-error" : undefined}
            {...form.register("orgName")}
          />
          {form.formState.errors.orgName && (
            <p
              id="orgName-error"
              role="alert"
              className="text-[12.5px] font-medium text-destructive"
            >
              {form.formState.errors.orgName.message}
            </p>
          )}
        </div>
        <Button type="submit" className={AUTH_PRIMARY_ACTION_CLASSES} disabled={isLoading}>
          {isLoading ? (
            t.common.creating
          ) : (
            <>
              {t.forms.createOrganization.submit}
              <ArrowRight className="h-[17px] w-[17px]" />
            </>
          )}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-[12.5px] uppercase tracking-wide text-muted-foreground">
          {t.auth.orContinueWith}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <section aria-labelledby="join-organization-heading" className="space-y-3">
        <h2 id="join-organization-heading" className={AUTH_LABEL_CLASSES}>
          {t.auth.joinExistingOrganization}
        </h2>
        {loadingInvites ? (
          <p role="status" className="text-[13.5px] text-muted-foreground">
            {t.auth.loadingInvitations}
          </p>
        ) : pendingInvites.length > 0 ? (
          <div className="divide-y divide-border">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between gap-3 py-3">
                <span className="min-w-0 break-words text-[13.5px] font-medium">
                  {invite.organisations?.name || `Organization ${invite.org_id}`}
                </span>
                <Button
                  size="sm"
                  className="shrink-0 rounded-lg"
                  onClick={() => handleAcceptInvite(invite)}
                  disabled={acceptingInvite === invite.invite_token}
                >
                  {acceptingInvite === invite.invite_token ? t.auth.accepting : t.auth.accept}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[13.5px] text-muted-foreground">{t.auth.noPendingInvites}</p>
        )}
      </section>

      <p className="mt-6 text-center text-[13.5px] text-muted-foreground [overflow-wrap:anywhere]">
        {sessionEmail && (
          <>{t.forms.createOrganization.signedInAs.replace("{email}", sessionEmail)} · </>
        )}
        <button
          type="button"
          onClick={handleLogout}
          disabled={isLoggingOut}
          className={`${AUTH_LINK_CLASSES} disabled:cursor-wait disabled:opacity-50`}
        >
          {t.forms.createOrganization.signOut}
        </button>
      </p>
    </AuthPageLayout>
  );
}
